import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Icon } from '@/lib/icons';
import { authApi } from '@/api/auth.api';
import { ScrollToTop } from '@/components/ui/scroll-to-top';
import { HeaderThemeToggle } from '@/components/layout/header/HeaderThemeToggle';

interface SectionInvalidScreenProps {
  onReconnected: () => void;
}

export function SectionInvalidScreen({ onReconnected }: SectionInvalidScreenProps) {
  const { t, user } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';

  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleReconnect = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || !user?.username) return;

    setIsSubmitting(true);
    setError(false);

    try {
      await authApi.login({ username: user.username, password: password.trim() });
      onReconnected();
    } catch {
      setError(true);
      setIsSubmitting(false);
    }
  }, [password, user?.username, onReconnected]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Minimal header bar with theme toggle */}
      <div className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <a
            href={`/${currentLang}`}
            className="flex items-center gap-2 text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            <Icon name="arrowLeft" className="h-4 w-4" />
            <span className="text-sm font-medium">{t.backToHome}</span>
          </a>

          <HeaderThemeToggle />
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        {/* Header — same pattern as ApiKeysPage */}
        <header className="relative py-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] lg:items-end">
            <div>
              <p className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-primary/70">
                {t.sectionInvalid}
              </p>
              <h1 className="mt-4 font-semibold tracking-tight text-foreground text-3xl sm:text-4xl">
                {user?.username || '—'}
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-muted-foreground/80 sm:text-base">
                {t.sectionInvalidDesc}
              </p>
            </div>

            {/* Signal cards — same pattern as ApiKeysPage */}
            <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-2xl border border-border/40 bg-background/60">
              <Signal label={t.username} value={user?.username || '—'} />
              <Signal label={t.role} value={user?.role || '—'} />
            </div>
          </div>
        </header>

        {/* Reconnect form */}
        <section className="py-7">
          <div className="max-w-md">
            <p className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-primary/60">
              {t.reEnterPasswordHint}
            </p>

            <form onSubmit={handleReconnect} className="mt-5 space-y-4">
              <div className="space-y-2">
                <label htmlFor="section-password" className="text-sm font-medium text-foreground/80">
                  {t.password}
                </label>
                <div className="relative">
                  <Input
                    ref={inputRef}
                    id="section-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(false); }}
                    placeholder={t.password}
                    autoComplete="current-password"
                    className="h-11 rounded-xl border-border/40 bg-background/60 pr-10 text-sm placeholder:text-muted-foreground/40 focus:border-primary/40 focus:ring-primary/20"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 transition-colors hover:text-foreground"
                    tabIndex={-1}
                  >
                    <Icon name={showPassword ? 'eyeOff' : 'eye'} className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3">
                  <Icon name="alertCircle" className="h-4 w-4 shrink-0 text-destructive" />
                  <p className="text-sm text-destructive">{t.loginFailedDesc}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={!password.trim() || isSubmitting}
                className="h-11 w-full rounded-full border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Icon name="loader2" className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Icon name="logIn" className="mr-2 h-4 w-4" />
                )}
                {t.reconnect}
              </Button>
            </form>
          </div>
        </section>

        {/* Go to login */}
        <section className="py-7">
          <a href={`/${currentLang}/login`}>
            <Button
              variant="outline"
              className="h-11 w-full rounded-full border-border/40 sm:w-auto bg-background/60 px-5 text-sm font-semibold text-foreground shadow-none transition-colors hover:bg-muted/30"
            >
              <Icon name="arrowRight" className="mr-2 h-4 w-4" />
              {t.orGoToLogin}
            </Button>
          </a>
        </section>
      </div>

      {/* Scroll to top */}
      <ScrollToTop />
    </div>
  );
}

function Signal({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-r border-border/40 px-4 py-3 last:border-r-0">
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">
        {label}
      </span>
      <span className="mt-1 block truncate text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}
